import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OnyxIconComponent, OnyxMessageComponent } from '@onyx/angular';
import type { Observable } from 'rxjs';
import { RoutePointStatusFormComponent } from '../../../../../../common/components/route/route-point/route-point-status-form/route-point-status-form.component';
import { RouteHelper } from '../../../../../../common/helpers/route.helper';
import { FleetIdentifierPipe } from '../../../../../../common/pipes/fleet-identifier.pipe';
import { RouteService } from '../../../../../../common/services/route.service';
import { TelematicsStore } from '../../../../../../common/stores/telematics.store';
import type { SimplifiedFleet } from '../../../../../fleet/common/interfaces/fleet';

export interface SwapStatusVehicleSet {
  vehicle: SimplifiedFleet;
  trailer: SimplifiedFleet | null;
}

@Component({
  selector: 'app-swap-status-form-set',
  imports: [
    TranslatePipe,
    RoutePointStatusFormComponent,
    OnyxMessageComponent,
    OnyxIconComponent,
    FleetIdentifierPipe,
  ],
  providers: [RouteService],
  templateUrl: './swap-status-form-set.component.html',
  styleUrl: './swap-status-form-set.component.scss',
})
export class SwapStatusFormSetComponent implements OnInit {
  private readonly routeService = inject(RouteService);
  private readonly telematicsStore = inject(TelematicsStore);
  private readonly injector = inject(Injector);

  public readonly swapUuid = input.required<string>();
  public readonly vehicleSet = input.required<SwapStatusVehicleSet>();
  public readonly showHeading = input(false);

  private readonly form = viewChild.required(RoutePointStatusFormComponent);

  public isDisabled = signal(false);
  public tooltip = signal<string | null>(null);

  protected point = computed(() =>
    this.telematicsStore
      .swapPointBySwapAndVehicleUuid()
      .get(this.swapUuid())
      ?.get(this.vehicleSet().vehicle.uuid),
  );

  public ngOnInit(): void {
    effect(
      () => {
        const telematicsItem = this.telematicsStore.getItem(
          this.vehicleSet().vehicle.uuid,
        );
        if (!telematicsItem) return;

        this.routeService.points.set(telematicsItem.routePoints);
        this.routeService.telematicsItem.set(telematicsItem);
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const point = this.point();
        if (!point) {
          this.isDisabled.set(false);
          this.tooltip.set(null);
          return;
        }

        const swapData = RouteHelper.getSwapData(point);
        const swapPoints =
          this.telematicsStore
            .swapPointsBySwapUuid()
            .get(swapData?.uuid ?? '') ?? null;
        const disabledData = RouteHelper.getChangeStatusState(
          point,
          this.telematicsStore.getItem(this.vehicleSet().vehicle.uuid),
          this.routeService,
        );

        const isSwapCompleted = !!swapPoints?.every((swapPoint) =>
          RouteHelper.isCompleted(swapPoint),
        );
        this.isDisabled.set(disabledData.disabled);
        this.tooltip.set(
          disabledData.tooltip ??
            (isSwapCompleted ? 'route.changeStatusDisabledBySwap' : null),
        );
      },
      { injector: this.injector },
    );
  }

  public validate(): Promise<boolean> {
    return this.form().validate();
  }

  public submit(): Observable<void> {
    return this.form().submit();
  }
}
